import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, } from "recharts";
import { Cascader } from 'antd';

function construirDatosGrafica(rows, { desde, hasta, meses = 12 } = {}) {
  const indice = {}
  for (const r of rows) {
    const clave = `${r.year}-${String(r.month).padStart(2, "0")}`
    indice[clave] = {
      ingresos: Number(r.ingresos) || 0,
      egresos: Number(r.egresos) || 0,
      year: r.year,
      month: r.month,
    }
  }

  let inicio, fin
  if (desde && hasta) {
    const d = new Date(desde)
    const h = new Date(hasta)
    inicio = new Date(d.getFullYear(), d.getMonth(), 1)
    fin = new Date(h.getFullYear(), h.getMonth(), 1)
  } else {
    const hoy = new Date();
    fin = new Date(hoy.getFullYear(), hoy.getMonth(), 1)
    inicio = new Date(fin.getFullYear(), fin.getMonth() - (meses - 1), 1)
  }

  const resultado = []
  const cursor = new Date(inicio)
  while (cursor <= fin) {
    const y = cursor.getFullYear()
    const m = cursor.getMonth() + 1
    const clave = `${y}-${String(m).padStart(2, "0")}`
    const r = indice[clave] || { ingresos: 0, egresos: 0, year: y, month: m }

    const etiqueta = new Date(r.year, r.month - 1, 1)
      .toLocaleDateString("es-MX", { month: "short", year: "2-digit" })
      .replace(".", "")

    resultado.push({
      label: etiqueta,
      ingresos: Number(r.ingresos ?? 0),
      egresos: Number(r.egresos ?? 0),
      _y: r.year,
      _m: r.month,
    })

    cursor.setMonth(cursor.getMonth() + 1)
  }

  return resultado
}

export default function GraficaDashboardProyectos({ desde, hasta }) {
  const [rows, setRows] = useState([])
  const [options, setOptions] = useState([])
  const [selected, setSelected] = useState([])

  useEffect(() => {
    axios
      .get("/proyectos/cascader")
      .then(({ data }) => setOptions(data))
      .catch(() => setOptions([]))
  }, [])

   useEffect(() => {
    (async () => {
      try {
        const params = {}
        if (desde && hasta) {
          params.desde = desde
          params.hasta = hasta
        }

        if (selected.length === 1) params.proyecto_id = selected[0]
        if (selected.length === 2) params.subproyecto_id = selected[1]

        const { data } = await axios.get("/dashboard-proyectos/ingresos-vs-egresos", { params })
        setRows(Array.isArray(data) ? data : [])
      } catch (e) {
        console.error("Error al cargar gráfica", e)
        setRows([])
      }
    })()
  }, [desde, hasta, selected])

  const data = useMemo(
    () => construirDatosGrafica(rows, { desde, hasta, meses: 12 }),
    [rows, desde, hasta]
  )

  const hasData = useMemo(
    () => data.some((d) => (d.ingresos ?? 0) > 0 || (d.egresos ?? 0) > 0),
    [data]
  )

  return (
    <div className="card card-chart">
      <div className="card-header-grafica">
        <label className="label-title">Ingresos vs Egresos</label>
        <Cascader
          style={{ width: "300px" }}
          size="large"
          options={options}
          changeOnSelect
          allowClear
          showSearch
          placeholder="Filtrar por Proyecto/Subproyecto"
          displayRender={(labels) => labels[labels.length - 1]}
          value={selected}
          onChange={(value) => setSelected(value || [])}
        />
      </div>

      {hasData ? (
        <div className="chart-wrapper">
          <ResponsiveContainer width="100%" height={480}>
            <BarChart data={data} barCategoryGap={12} margin={{ top: 20, right: 30, left: 60, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" />
              <YAxis
                domain={[0, "dataMax"]}
                tickFormatter={(v) => v.toLocaleString("es-MX")}
              />
              <Tooltip
                formatter={(v) =>
                  v.toLocaleString("es-MX", {
                    style: "currency",
                    currency: "MXN",
                  })
                }
                labelFormatter={(_label, payload) => {
                  const p = payload?.[0]?.payload;
                  return p ? `Mes: ${p.label}` : "";
                }}
              />
              <Legend />
              <Bar
                dataKey="egresos"
                name="Egresos"
                fill="#F97316"
                radius={[4, 4, 0, 0]}
                barSize={22}
              />
              <Bar
                dataKey="ingresos"
                name="Ingresos"
                fill="#4F46E5"
                radius={[4, 4, 0, 0]}
                barSize={22}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="card-empty">Aún no hay datos para graficar</div>
      )}
    </div>
  );
}
